import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../env';
import { AppLogger, AppLoggerService } from '../../core/logging';
import { RecipeDraftPayload, RecipeDraftStep } from '../dialog/dialog.models';

export type RecipeImportStepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | string;

export type RecipeImportStepDto = {
  step: string;
  status: RecipeImportStepStatus;
  message?: string;
  started_at_unix?: number;
  finished_at_unix?: number;

  // allow backend to evolve
  [key: string]: any;
};

// Same wire shape as the dialog assistant's `propose_recipe` payload (both are
// backed by the same "Pantry Recipe Format" on the backend, see
// internal/domain/recipe/format.go) — reuse those types rather than
// redeclaring them, and reuse the shared `ui-recipe-preview` card to render them.
export type RecipeDraftStepDto = RecipeDraftStep;
export type RecipeDraftDto = Omit<RecipeDraftPayload, 'recipe_id'>;

// One image the crawler found for the imported page, surfaced for review.
// `preview_url` is a backend-proxied, auth-protected URL — render it by fetching
// the bytes via getImportJobImage() (a raw <img src> would not carry the auth
// token). `default_title` marks the image to pre-select as the title.
export type ProposedImageDto = {
  name: string;
  role: 'title' | 'content' | string;
  preview_url: string;
  default_title: boolean;
  score?: number;
  content_type?: string;
  source_url?: string;
};

// The user's decision for one proposed image, sent to saveImportJobDraft().
export type ImportImageSelectionDto = {
  name: string;
  keep: boolean;
  is_title: boolean;
};

export type RecipeImportJobDto = {
  id: string;
  target_url: string;
  owner_user_id?: number;
  crawler_task_id?: string;
  created_recipe_id?: number;
  status?: 'running' | 'draft_ready' | 'succeeded' | 'failed' | string;
  state_message?: string;
  steps?: RecipeImportStepDto[];
  // Populated once status reaches 'draft_ready': the AI-extracted recipe,
  // held for review — nothing is persisted until saveImportJobDraft() is called.
  draft?: RecipeDraftDto | null;
  // Images the crawler proposed for the page (best-effort, possibly empty),
  // populated alongside `draft`. The user keeps/discards and picks a title.
  proposed_images?: ProposedImageDto[];
  created_at?: string;
  updated_at?: string;

  // allow backend to evolve
  [key: string]: any;
};

export type RecipeImportCreateResponse = {
  job: RecipeImportJobDto;
};

export type RecipeImportStatusResponse = {
  job: RecipeImportJobDto;
};

@Injectable({ providedIn: 'root' })
export class RecipeImportService {
  private readonly log: AppLogger;

  constructor(
    private http: HttpClient,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(RecipeImportService);
  }

  /**
   * Start an import job.
   * POST /api/recipes/import
   */
  startImport(targetUrl: string, householdId: number): Observable<RecipeImportCreateResponse> {
    const payload = { target_url: targetUrl, household_id: householdId };
    this.log.debug('Start recipe import', payload);

    return this.http.post<RecipeImportCreateResponse>(
      `${environment.apiUrl}/recipes/import`,
      payload
    );
  }

  /**
   * Poll import job status.
   * GET /api/recipes/import/:id
   */
  getImportJob(jobId: string): Observable<RecipeImportStatusResponse> {
    this.log.debug('Get recipe import job', { jobId });

    return this.http.get<RecipeImportStatusResponse>(
      `${environment.apiUrl}/recipes/import/${jobId}`
    );
  }

  /**
   * Fetch the crawler-captured page thumbnail (PNG) for an import job.
   * GET /api/recipes/import/:id/thumbnail
   *
   * Returns the raw image as a Blob (wrap it in an object URL to render).
   * The backend answers 425 (Too Early) while the page hasn't been captured
   * yet, which surfaces here as an HTTP error — callers should keep polling.
   */
  getImportJobThumbnail(jobId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/recipes/import/${jobId}/thumbnail`,
      { responseType: 'blob' }
    );
  }

  /**
   * Fetch a proposed image's bytes for preview. The endpoint is auth-protected,
   * so we fetch it as a Blob via HttpClient (which attaches the token) and the
   * caller wraps it in an object URL — the same approach as the thumbnail.
   * GET /api/recipes/import/:id/images/:name
   */
  getImportJobImage(jobId: string, name: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/recipes/import/${jobId}/images/${encodeURIComponent(name)}`,
      { responseType: 'blob' }
    );
  }

  /**
   * Persist the reviewed draft of a `draft_ready` job as a real recipe.
   * POST /api/recipes/import/:id/save
   *
   * `images` carries the user's image selection (which proposed images to keep
   * and which is the title). Omit it to keep only the crawler's title image.
   */
  saveImportJobDraft(
    jobId: string,
    images?: ImportImageSelectionDto[]
  ): Observable<RecipeImportStatusResponse> {
    this.log.debug('Save recipe import job draft', { jobId, images });

    const body = images && images.length > 0 ? { images } : {};
    return this.http.post<RecipeImportStatusResponse>(
      `${environment.apiUrl}/recipes/import/${jobId}/save`,
      body
    );
  }

  /**
   * Start an images-only re-crawl of an existing recipe's source page, so a
   * recipe imported before the images feature can adopt images from it.
   * POST /api/recipes/:recipeId/images/refetch
   *
   * The returned job is polled via getImportJob() and its proposed images'
   * previews are fetched via getImportJobImage() — exactly like an import job.
   * Apply the user's selection with applyImageRefetch().
   */
  startImageRefetch(recipeId: number): Observable<RecipeImportStatusResponse> {
    this.log.debug('Start recipe image refetch', { recipeId });

    return this.http.post<RecipeImportStatusResponse>(
      `${environment.apiUrl}/recipes/${recipeId}/images/refetch`,
      {}
    );
  }

  /**
   * Apply the selected images from a completed image-refetch job onto its
   * target recipe. Omit `images` to keep only the source page's title image.
   * POST /api/recipes/import/:jobId/apply-images
   */
  applyImageRefetch(
    jobId: string,
    images?: ImportImageSelectionDto[]
  ): Observable<RecipeImportStatusResponse> {
    this.log.debug('Apply recipe image refetch', { jobId, images });

    const body = images && images.length > 0 ? { images } : {};
    return this.http.post<RecipeImportStatusResponse>(
      `${environment.apiUrl}/recipes/import/${jobId}/apply-images`,
      body
    );
  }
}

